/**
 * Local mirrors of the (final) `@loot-os/api-contract` shapes plus the thin
 * adapter interface this package codes against. Defined locally so this
 * package's typecheck never depends on `@loot-os/client`'s dist output
 * existing (it is built in parallel); the runtime dependency stays in
 * package.json for publishing.
 */

/**
 * Where an item comes from: the Loot OS catalog, a vendor marketplace listing, or a voucher the
 * house issued. `VOUCHER` reaches the wire on an inventory unit a cancelled marketplace order
 * paid back, never on a box a shopper can open.
 */
export type DropTableItemSource = "CATALOG" | "MARKETPLACE" | "VOUCHER";

/** One row of a box's drop table. Weights across a table sum to exactly 1_000_000. */
export interface DropTableItem {
  itemId: string;
  itemSource: DropTableItemSource;
  name: string;
  imageUrl: string;
  value: number;
  weight: number;
  /** The vendor's asking price in US cents on a MARKETPLACE entry, absent on every other source. */
  listingPriceCents?: number;
  /** A counted number of this item can be won: every MARKETPLACE entry, and a FIXED catalog item. */
  isLimited?: boolean;
  /** How many of a limited entry remain as of the box's last refresh; null when unlimited. */
  limitedQuantity?: number | null;
  /** The item can no longer be shipped: a shopper who wins it owns it and sells it for its value. */
  isGemSubstitute?: boolean;
}

/**
 * A box's art beyond `imageUrl`: a background behind it and an overlay above it, and a mobile set
 * of all three. "" = none; an empty mobile slot falls back to the desktop one.
 */
export interface BoxArt {
  background: string;
  overlay: string;
  mobile: { image: string; background: string; overlay: string };
}

/**
 * A box category: a game (`POKEMON`, `BASEBALL`, `YUGIOH`) or a family of games (`SPORTS`,
 * `OTHER`), from boxed's fixed list, in the order a storefront lists them.
 */
export type BoxCategory =
  | "POKEMON"
  | "MTG"
  | "ONE_PIECE"
  | "RIFTBOUND"
  | "SPORTS"
  | "BASEBALL"
  | "BASKETBALL"
  | "FOOTBALL"
  | "HOCKEY"
  | "SOCCER"
  | "RACING"
  | "COMBAT_SPORTS"
  | "GOLF"
  | "OTHER"
  | "LORCANA"
  | "DRAGON_BALL"
  | "STAR_WARS"
  | "YUGIOH"
  | "DIGIMON"
  | "GUNDAM"
  | "COUNTERSTRIKE"
  | "RUST"
  | "NFTS"
  | "REDDIT_AVATARS"
  | "PARALLEL";

export interface BoxSummary {
  id: string;
  name: string;
  slug: string;
  imageUrl: string;
  priceCents: number;
  version: number;
  /** True when the box comes from the shared Loot OS catalog. */
  isCatalog: boolean;
  /** Merchandising: the storefront home page's featured row shows these and nothing else. */
  featured: boolean;
  openedCount: number;
  /** Absent from a server that predates box art. */
  art?: BoxArt;
  /** When the box was added; absent from a server that predates it. */
  createdAt?: string;
  /**
   * The most valuable item that can drop, for a browse tile. Sent on the box lists only; absent
   * when no entry can drop, and from a server that predates it.
   */
  topItem?: BoxTopItem;
  /**
   * The box categories the box is in, in list order: every game its current version's catalog
   * items name, and each game's family. Sent by `listBoxes` and `getBox`; `[]` when no item names
   * a game, absent from a server that predates it.
   */
  categories?: BoxCategory[];
  /**
   * How swingy the box is: one over the chance of rolling an item worth at least its expected
   * value, read on a 0–16 scale in four even levels (low, medium, high, max). Unclamped: a
   * one-in-10,000 chase sends 10,000, and anything above 16 reads as max. Sent by the box lists
   * and the box detail; null when no entry can drop, absent from a server that predates it.
   */
  volatility?: number | null;
  /**
   * The box's own tags as stored on the box, which a tenant copying a box template takes as the
   * copy's tags. `[]` when the box has none, absent from a server that predates it.
   */
  tags?: string[];
}

/** A box's most valuable item that can drop. `value` is integer cents. */
export interface BoxTopItem {
  name: string;
  imageUrl: string;
  value: number;
}

export interface BoxDetail extends BoxSummary {
  ev: number;
  dropTable: DropTableItem[];
  /** sha256 of this version's entries: the commitment an open settles against. */
  dropTableHash: string;
  /** When this version was written. */
  refreshedAt: string;
  /**
   * Set on a forge box (a box a shopper built): who forged it and the cut this version pays them.
   * null on every other box; absent from a server that predates the forge.
   */
  forge?: ForgeBoxInfo | null;
  /** The opener style this box opens with; absent from a server that predates opener styles. */
  opener?: BoxOpener;
}

/**
 * The opener style a box opens with: `kind` picks the animation (`OPENER_DESCRIPTORS`; an unknown
 * one opens as a spinner) and `theme` overrides the host's theme token by token.
 */
export interface BoxOpener {
  styleId: string;
  kind: string;
  theme?: {
    colors?: Partial<
      Record<
        "background" | "surface" | "border" | "text" | "textMuted" | "accent" | "accentForeground",
        string
      >
    >;
    rarity?: Partial<Record<"0" | "1" | "2" | "3" | "4", string>>;
    radius?: string;
    fontFamily?: string;
    /** The kind's own theme variables (hex colors keyed by variable). */
    kind?: Record<string, string>;
  };
  /**
   * The tenant's logo as SVG source, drawn onto the opener as its brand; absent when it has none.
   * Embed it only as an `<img>` / `Image()` data URI (`logoDataUri`), never as inline markup.
   */
  logoSvg?: string;
  /**
   * The tenant's name, printed as text where the opener draws the logo when the box carries no
   * `logoSvg`; absent when it has none.
   */
  brandName?: string;
}

/** Who forged a forge box, as other shoppers see them, and the cut its version pays them. */
export interface ForgeBoxInfo {
  /** The creator's username, else `Player XXXX`. */
  creatorDisplayName: string;
  /** Percent, 0–3, two decimals at most. */
  creatorCutPercent: number;
}

export interface OpenEntry {
  openId: string;
  nonce: number;
  result: number;
  itemIndex: number;
  item: DropTableItem;
}

export interface FairnessBundle {
  seedDate: string;
  serverSeedHash: string;
  clientSeed: string;
  salt: string;
  nonces: number[];
}

export interface ClaimedItem {
  openId: string;
  inventoryItemId: string;
  item: DropTableItem;
  /** The entry's isLimited as of the open; false for an item won before the field existed. */
  isLimited: boolean;
}

export interface ClaimResponse {
  openGroupId: string;
  costCents: number;
  balanceAfterCents: number;
  claimedAt: string;
  items: ClaimedItem[];
}

/**
 * A claim followed, in the same transaction, by selling every claimed item back for its value;
 * `balanceAfterCents` is the balance after both movements.
 */
export interface SellBackResponse extends ClaimResponse {
  /** Σ item.value credited by the sale. */
  soldCents: number;
  soldAt: string;
}

/**
 * What selling owned units back to the house credited. The per-item Sell after a claim sends one
 * id, so `items` holds one entry; the batch endpoint the storefront uses sends several.
 */
export interface SellInventoryResponse {
  /** Σ item.value credited by the sale. */
  soldCents: number;
  balanceAfterCents: number;
  soldAt: string;
  items: { inventoryItemId: string; item: DropTableItem }[];
}

export interface OpenResponse {
  openGroupId: string;
  boxId: string;
  boxVersion: number;
  /** Total purchase price of the revealed items, in US cents. Debited only at claim. */
  costCents: number;
  status: "unclaimed" | "claimed";
  /** Set iff the server auto-claimed (status "claimed"). */
  claim: ClaimResponse | null;
  entries: OpenEntry[];
  fairness: FairnessBundle;
  /** sha256 of the entries of version `boxVersion`: the table these rolls walked. */
  dropTableHash: string;
}

/** An outstanding reveal fetched cold (page load); enough to render the claim UI. */
export interface UnclaimedReveal {
  openGroupId: string;
  boxId: string;
  boxVersion: number;
  costCents: number;
  /** What selling the reveal back instead of claiming it credits: Σ entries[].item.value. */
  sellBackCents: number;
  createdAt: string;
  entries: OpenEntry[];
}

/** An `OpenResponse` fabricated locally in demo mode (no session token). */
export interface DemoOpenResponse extends OpenResponse {
  demo: true;
}

/** Narrowing helper for demo-mode responses. */
export function isDemoOpenResponse(response: OpenResponse): response is DemoOpenResponse {
  return (response as DemoOpenResponse).demo === true;
}

/** Internal cents per displayed whole unit: 100 shows 1,250 cents as 12.50; 1 shows 1,250. */
export type CurrencyMultiplier = 1 | 10 | 100 | 1000;

/**
 * How the environment displays cents — presentation only. Every amount on the wire is an
 * integer count of US cents; `formatAmount` in `./currency` turns one into what the shopper
 * reads. Pass the storefront's currency as the `currency` prop; credits (`$12.50`) by default.
 */
export interface Currency {
  /** Singular unit name, e.g. "credit". */
  name: string;
  /** Plural unit name, resolved server-side. */
  namePlural: string;
  /** Prefix written with no space before the amount ("$12.50"); "" for none ("1,250 coins"). */
  symbol: string;
  multiplier: CurrencyMultiplier;
  /** Tenant-uploaded icon as SVG source, "" when unset; render only through an <img> data URI. */
  iconSvg: string;
}

export interface BalanceResponse {
  /** Integer US cents. */
  balanceCents: number;
  /**
   * Cents held by an unclaimed box reveal: its price, moved into the hold at the open and released
   * by the claim. A battle seat holds nothing (its entry is debited when it is taken). Spendable =
   * balanceCents − heldCents; show that, not balanceCents.
   */
  heldCents: number;
  currency: Currency;
}

export interface SeedReveal {
  date: string;
  seedHash: string;
  /** null until the seed's UTC day has fully passed. */
  seed: string | null;
}

export type LootErrorCode =
  | "unauthorized"
  | "forbidden"
  | "session_expired"
  | "not_found"
  | "insufficient_balance"
  | "box_version_changed"
  | "box_not_active"
  | "box_not_openable"
  | "invalid_quantity"
  | "invalid_client_seed"
  | "reveal_pending"
  | "purchase_price_mismatch"
  | "invalid_drop_table"
  | "duplicate"
  | "opens_paused"
  | "account_locked"
  | "fraud_declined"
  | "withdrawal_limit_exceeded"
  | "kyc_required"
  | "rate_limited"
  | "payments_unavailable"
  | "invalid_request"
  | "tenant_archived"
  | "battles_disabled"
  | "battle_not_joinable"
  | "battle_seat_taken"
  | "battle_already_joined"
  | "battle_not_creator"
  | "invalid_battle_settings"
  | "battle_price_mismatch"
  | "battle_not_completed"
  | "battle_not_stale"
  | "realtime_unavailable"
  | "invalid_shipping_address"
  | "shipping_unavailable"
  | "shipping_address_required"
  | "unsupported_shipping_destination"
  | "shipping_fee_mismatch"
  | "address_unverifiable"
  | "address_country_mismatch"
  | "shipping_config_conflict"
  | "username_taken"
  | "reveal_already_sold"
  | "reveal_already_claimed"
  | "inventory_item_not_withdrawable"
  | "inventory_item_not_sellable"
  | "chat_disabled"
  | "chat_muted"
  | "chat_message_rejected"
  | "chat_message_not_found"
  | "chat_room_not_found"
  | "chat_report_not_open"
  | "username_required"
  | "forge_disabled"
  | "forge_drop_table_invalid"
  | "forge_cut_invalid"
  | "forge_daily_limit"
  | "forge_nothing_to_claim"
  | "forge_name_rejected"
  | "forge_image_invalid"
  | "order_not_cancellable"
  | "fulfillment_unavailable"
  | "shopify_shop_not_linked"
  | "shopify_shop_ineligible"
  | "shopify_box_ineligible"
  | "shopify_billing_required"
  | "shopify_reveal_token_invalid"
  | "box_purchase_not_revealable"
  | "store_credit_short"
  | "store_credit_unavailable"
  | "gift_code_not_redeemable"
  | "live_breaks_disabled"
  | "live_break_host_not_allowed"
  | "live_break_already_open"
  | "live_break_closed"
  | "live_break_ended"
  | "live_break_not_accepting_purchases"
  | "live_break_blocked"
  | "live_break_box_not_eligible"
  | "live_break_price_changed"
  | "live_break_queue_full"
  | "live_break_sequence_out_of_date"
  | "live_break_reveal_in_progress"
  | "live_break_auto_mode_on"
  | "live_break_entry_not_cancellable"
  | "live_break_entry_not_pickable"
  | "live_break_title_rejected"
  | "live_break_settings_invalid"
  | "live_break_twitch_channel_not_linked"
  | "live_break_twitch_channel_not_found"
  | "live_break_playlist_invalid"
  | "live_break_not_announceable"
  | "live_break_nothing_to_claim"
  | "twitch_not_enabled"
  | "twitch_unavailable"
  | "twitch_app_invalid"
  | "twitch_code_rejected"
  | "twitch_not_linked"
  | "twitch_already_linked"
  | "internal";

/** Structural mirror of `LootApiError` from `@loot-os/client`. */
export interface LootApiErrorLike {
  code: LootErrorCode | (string & {});
  status: number;
  message: string;
  details?: Record<string, unknown>;
}

/** Structural guard for errors thrown by the API client (or a stub). */
export function isLootApiErrorLike(error: unknown): error is LootApiErrorLike {
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as Partial<LootApiErrorLike>;
  return typeof candidate.code === "string" && typeof candidate.message === "string";
}

export interface OpenBoxParams {
  boxId: string;
  clientSeed: string;
  amount?: number;
  expectedVersion?: number;
  /** Caller-supplied so a transport retry replays the same open. */
  idempotencyKey?: string;
}

/**
 * The thin adapter interface `LootBoxOpener` codes against — exactly the
 * `LootClient` methods this package uses, so a stub works for tests/dev and
 * any compatible client can be injected via the `client` prop.
 */
export interface LootApiLike {
  getBox(boxId: string): Promise<BoxDetail>;
  openBox(params: OpenBoxParams): Promise<OpenResponse>;
  claimReveal(params: {
    openGroupId: string;
    expectedPurchaseAmount?: number;
  }): Promise<{ claim: ClaimResponse; replayed: boolean }>;
  /** Optional: without it the opener offers Claim alone, never Sell back. */
  sellBackReveal?(params: {
    openGroupId: string;
    expectedPurchaseAmount?: number;
  }): Promise<{ sale: SellBackResponse; replayed: boolean }>;
  /** Optional: without it the opener offers no per-item Sell on a claimed reveal. */
  sellInventoryItem?(params: { inventoryItemId: string }): Promise<SellInventoryResponse>;
  getUnclaimedReveal?(): Promise<UnclaimedReveal | null>;
  updateMySettings?(params: {
    autoClaim?: boolean;
    soundMuted?: boolean;
    soundVolume?: number;
  }): Promise<unknown>;
  getSeed?(date: string): Promise<SeedReveal>;
  setSessionToken?(token: string | null): void;
}

/** Constructor config for the default `LootClient` (mirrors `@loot-os/client`). */
export interface LootClientConfig {
  publishableKey: string;
  baseUrl?: string;
  sessionToken?: string | null;
  onSessionExpired?: () => Promise<string | null>;
}

/** Factory used to construct the API client when no `client` prop is given. */
export type CreateLootClient = (config: LootClientConfig) => LootApiLike | Promise<LootApiLike>;
